import { findCountryCode } from "@redotech/locale/countries";
import { returnsCenterIdFormat } from "@redotech/redo-model/ids";
import {
  DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY,
  getInventoryPushMinimumQuantity,
} from "@redotech/redo-model/inventory/inventory-push-buffer";
import {
  defaultReceivingLabelField,
  ReceivingLabelField,
} from "@redotech/redo-model/inventory/receiving-label";
import {
  defaultRedoManagedReturnsSortingMode,
  RedoManagedReturnsSortingMode,
} from "@redotech/redo-model/returns/sorting-types";
import {
  ConsolidationShipToAddress,
  DEFAULT_RETURNS_PROCESSING_COST,
  ReturnProcessingLevel,
  ReturnsCenterConfig,
  Team,
} from "@redotech/redo-model/team";
import {
  ExternalLocationHubConfig,
  ExternalReturnHubType,
} from "@redotech/redo-model/team/address";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { deepIntersectionEquals } from "@redotech/util/equal";

export interface ReturnsCenterConfigFormValue {
  returnsCenterId: string;
  consolidationScheduleDays: string;
  shipToName: string;
  shipToStreet1: string;
  shipToStreet2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  refulfillment: boolean;
  processingFee: string;
}

export interface ExternalLocationHubFormConfig {
  provider: ExternalReturnHubType | "";
  locationId: string;
}

function formatCents(value: number): string {
  return value.toFixed(2);
}

function formatCentsOptional(value: number | undefined | null): string {
  if (value == null || value === 0) {
    return "";
  }
  return value.toFixed(2);
}

function validateInventoryPushMinimumQuantity(value: string): string[] {
  const trimmed = value.trim();
  const parsed = Number(trimmed);
  if (!/^\d+$/.test(trimmed) || !Number.isSafeInteger(parsed)) {
    return ["Enter a whole number of 0 or more."];
  }
  return [];
}

function parseInventoryPushMinimumQuantity(value: string): number {
  const errors = validateInventoryPushMinimumQuantity(value);
  if (errors.length > 0) {
    return DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY;
  }
  return Number(value);
}

export const availableReturnsCentersInput = input<
  ReturnsCenterConfigFormValue[]
>({ equal: deepIntersectionEquals });

export const redoManagedReturnsSettingsForm = groupInput({
  enabled: input<boolean>(),
  processingCost: input<string>({ validator: numberValidator({ min: 0 }) }),
  processingCoverageShare: input<string>({
    validator: numberValidator({ min: 0 }),
  }),
  consolidationCoverageShare: input<string>({
    validator: numberValidator({ min: 0 }),
  }),
  returnProcessingLevel: input<ReturnProcessingLevel>(),
  sortingMode: input<RedoManagedReturnsSortingMode>(),
  inventoryPushMinimumQuantity: input<string>({
    validator: validateInventoryPushMinimumQuantity,
  }),
  receivingLabelEnabled: input<boolean>(),
  receivingLabelEncodedField: input<ReceivingLabelField>(),
  externalLocationHubMappings: input<
    Record<string, ExternalLocationHubFormConfig>
  >({ equal: deepIntersectionEquals }),
  availableReturnsCenters: availableReturnsCentersInput,
});

export type RedoManagedReturnsSettingsForm = InputSchema.Input<
  typeof redoManagedReturnsSettingsForm
>;
export type RedoManagedReturnsSettingsValue = InputSchema.Value<
  typeof redoManagedReturnsSettingsForm
>;

export const redoManagedReturnsSettingsDefault: RedoManagedReturnsSettingsValue =
  {
    enabled: false,
    processingCost: `${DEFAULT_RETURNS_PROCESSING_COST}`,
    processingCoverageShare: "",
    consolidationCoverageShare: "",
    returnProcessingLevel: ReturnProcessingLevel.VERIFICATION,
    sortingMode: defaultRedoManagedReturnsSortingMode,
    inventoryPushMinimumQuantity: `${DEFAULT_INVENTORY_PUSH_MINIMUM_QUANTITY}`,
    receivingLabelEnabled: false,
    receivingLabelEncodedField: defaultReceivingLabelField,
    externalLocationHubMappings: {},
    availableReturnsCenters: [],
  };

export const redoManagedReturnsTeamToFormValue = (team: {
  settings: Pick<Team["settings"], "redoManagedReturns">;
}): RedoManagedReturnsSettingsValue => {
  const storedMappings =
    team.settings.redoManagedReturns?.externalLocationHubMappings;
  const formMappings: Record<string, ExternalLocationHubFormConfig> = {};
  if (storedMappings) {
    for (const [key, value] of Object.entries(storedMappings)) {
      // DB stores raw numeric keys; convert to formatted for form display
      const formattedKey =
        key && !returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.write(BigInt(key))
          : key;
      formMappings[formattedKey] = {
        provider: value.provider,
        locationId: value.locationId ?? "",
      };
    }
  }

  const storedCenters =
    team.settings.redoManagedReturns?.availableReturnsCenters ?? [];
  const formCenters: ReturnsCenterConfigFormValue[] = storedCenters.map(
    (config) => {
      const formattedCenterId =
        config.returnsCenterId &&
        !returnsCenterIdFormat.test(config.returnsCenterId)
          ? returnsCenterIdFormat.write(BigInt(config.returnsCenterId))
          : config.returnsCenterId;
      return {
        returnsCenterId: formattedCenterId,
        consolidationScheduleDays:
          config.consolidationScheduleDays?.toString() ?? "",
        shipToName: config.shipToAddress?.name ?? "",
        shipToStreet1: config.shipToAddress?.street1 ?? "",
        shipToStreet2: config.shipToAddress?.street2 ?? "",
        shipToCity: config.shipToAddress?.city ?? "",
        shipToState: config.shipToAddress?.state ?? "",
        shipToZip: config.shipToAddress?.zip ?? "",
        shipToCountry:
          findCountryCode(config.shipToAddress?.country ?? "") ??
          config.shipToAddress?.country ??
          "",
        refulfillment: config.refulfillment ?? false,
        processingFee: formatCentsOptional(config.processingFee),
      };
    },
  );

  return {
    enabled: team.settings.redoManagedReturns?.enabled ?? false,
    processingCost: formatCents(
      team.settings.redoManagedReturns?.processingCost ??
        DEFAULT_RETURNS_PROCESSING_COST,
    ),
    processingCoverageShare: formatCentsOptional(
      team.settings.redoManagedReturns?.processingCoverageShare,
    ),
    consolidationCoverageShare: formatCentsOptional(
      team.settings.redoManagedReturns?.consolidationCoverageShare,
    ),
    returnProcessingLevel:
      team.settings.redoManagedReturns?.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    sortingMode:
      team.settings.redoManagedReturns?.sortingMode ??
      defaultRedoManagedReturnsSortingMode,
    inventoryPushMinimumQuantity: getInventoryPushMinimumQuantity(
      team.settings.redoManagedReturns,
    ).toString(),
    receivingLabelEnabled:
      team.settings.redoManagedReturns?.receivingLabel?.enabled ?? false,
    receivingLabelEncodedField:
      team.settings.redoManagedReturns?.receivingLabel?.encodedField ??
      defaultReceivingLabelField,
    externalLocationHubMappings: formMappings,
    availableReturnsCenters: formCenters,
  };
};

export const redoManagedReturnsFormValueToTeam = (
  redoManagedReturnsSettings: RedoManagedReturnsSettingsValue,
): Team["settings"]["redoManagedReturns"] => {
  const mappings = redoManagedReturnsSettings.externalLocationHubMappings;
  const hubMappings: Record<string, ExternalLocationHubConfig> = {};
  if (mappings) {
    for (const [key, value] of Object.entries(mappings)) {
      if (value.provider) {
        // Convert formatted key (rctr_...) back to raw numeric for DB storage
        const rawKey = returnsCenterIdFormat.test(key)
          ? returnsCenterIdFormat.read(key).toString()
          : key;
        hubMappings[rawKey] = {
          provider: value.provider,
          locationId:
            value.locationId && value.locationId.trim().length > 0
              ? value.locationId
              : undefined,
        };
      }
    }
  }

  const formCenters = redoManagedReturnsSettings.availableReturnsCenters ?? [];
  const availableReturnsCenters: ReturnsCenterConfig[] = formCenters.map(
    (c) => {
      const rawCenterId = returnsCenterIdFormat.test(c.returnsCenterId)
        ? returnsCenterIdFormat.read(c.returnsCenterId).toString()
        : c.returnsCenterId;

      const hasAddress = c.shipToName || c.shipToStreet1;
      const shipToAddress: ConsolidationShipToAddress | undefined = hasAddress
        ? {
            name: c.shipToName,
            street1: c.shipToStreet1,
            street2: c.shipToStreet2 || undefined,
            city: c.shipToCity,
            state: c.shipToState || undefined,
            zip: c.shipToZip,
            country: c.shipToCountry,
          }
        : undefined;

      const days = Number(c.consolidationScheduleDays);
      const processingFee = Number(c.processingFee);

      return {
        returnsCenterId: rawCenterId,
        consolidationScheduleDays:
          c.consolidationScheduleDays === "" || isNaN(days) ? undefined : days,
        shipToAddress,
        refulfillment: c.refulfillment ?? undefined,
        processingFee:
          c.processingFee === "" || isNaN(processingFee)
            ? undefined
            : processingFee,
      };
    },
  );

  const processingCost = isNaN(
    Number(redoManagedReturnsSettings.processingCost),
  )
    ? DEFAULT_RETURNS_PROCESSING_COST
    : Number(redoManagedReturnsSettings.processingCost);
  return {
    enabled: redoManagedReturnsSettings.enabled,
    processingFee: processingCost,
    processingCost,
    processingCoverageShare:
      Number(redoManagedReturnsSettings.processingCoverageShare) || 0,
    consolidationCoverageShare:
      Number(redoManagedReturnsSettings.consolidationCoverageShare) || 0,
    returnProcessingLevel:
      redoManagedReturnsSettings.returnProcessingLevel ??
      ReturnProcessingLevel.VERIFICATION,
    sortingMode: redoManagedReturnsSettings.sortingMode,
    inventoryPushMinimumQuantity: parseInventoryPushMinimumQuantity(
      redoManagedReturnsSettings.inventoryPushMinimumQuantity,
    ),
    receivingLabel: {
      enabled: redoManagedReturnsSettings.receivingLabelEnabled,
      encodedField: redoManagedReturnsSettings.receivingLabelEncodedField,
    },
    externalLocationHubMappings:
      Object.keys(hubMappings).length > 0 ? hubMappings : undefined,
    availableReturnsCenters:
      availableReturnsCenters.length > 0 ? availableReturnsCenters : undefined,
  };
};
